import { type ReactElement, type RefObject } from 'react';
import type {
  GroupChannelCreateParams,
  Member,
} from '@sendbird/chat/groupChannel';
import type { User } from '@sendbird/chat';
import { useSendbirdStateContext } from '@sendbird/uikit-react';
import { useSendbirdContext } from './SendbirdProviderWithSDK';
import { useLocalization } from '@sendbird/uikit-react/hooks/useLocalization';
import { MenuItems } from '@sendbird/uikit-react/ui/ContextMenu';
import { Avatar, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';

// Based on:
// - https://github.com/sendbird/sendbird-uikit-react/blob/main/src/ui/UserProfile/index.tsx
// - https://github.com/sendbird/sendbird-uikit-react/blob/main/src/ui/MessageContent/MessageProfile/index.tsx
// ^ Customization is to make 1-to-1 channels have `isDistinct: true`
// ^ Also uses `RenderUserProfileProps` instead of the custom UserProfile-specific Props that Sendbird uses

interface Props {
  user: User | Member;
  currentUserId: string;
  avatarRef: RefObject<any>;
  close(): void;
}

function UserProfile({
  close,
  user,
  currentUserId,
  avatarRef,
}: Props): ReactElement {
  const state = useSendbirdStateContext();
  const { sb } = useSendbirdContext();
  const { stringSet } = useLocalization();
  const currentUserId_ = currentUserId || state.config.userId;

  return (
    <MenuItems
      closeDropdown={close}
      parentContainRef={avatarRef}
      parentRef={avatarRef}
      style={{ paddingTop: '0px', paddingBottom: '0px' }}
    >
      <div className="sendbird__user-profile bg-popover text-popover-foreground">
        <section className="sendbird__user-profile-avatar flex justify-center">
          <Avatar size="lg" className="size-20">
            <AvatarImage src={user.profileUrl} alt={user.nickname} />
          </Avatar>
        </section>
        <section className="sendbird__user-profile-name text-center">
          <p className="text-lg font-medium">
            {user.nickname || stringSet.NO_NAME}
          </p>
        </section>
        {user.userId !== currentUserId_ && (
          <section className="sendbird__user-profile-message">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                // Create 1:1 channel
                const params: GroupChannelCreateParams = {
                  isDistinct: true,
                  invitedUserIds: user.userId ? [user.userId] : [],
                  operatorUserIds: [currentUserId_],
                };
                sb?.groupChannel
                  .createChannel(params)
                  .then((groupChannel) => {
                    state.config.onStartDirectMessage?.(groupChannel);
                  })
                  .catch((err: unknown) => console.error(err));
              }}
            >
              {stringSet.USER_PROFILE__MESSAGE}
            </Button>
          </section>
        )}
        <div className="sendbird__user-profile-separator" />
        <section className="sendbird__user-profile-userId flex flex-col gap-1 px-4 py-2">
          <span className="sendbird__user-profile-userId--label text-xs text-muted-foreground">
            {stringSet.USER_PROFILE__USER_ID}
          </span>
          <span className="sendbird__user-profile-userId--value break-all text-sm">
            {user.userId}
          </span>
        </section>
      </div>
    </MenuItems>
  );
}

export default UserProfile;
